import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { useAssetLookup } from "../api/useAssetLookup";
import { AssetIcon } from "../components/AssetIcon";
import { formatGameTime } from "../format/formatGameTime";
import { formatPickRate } from "../format/formatPickRate";
import { formatSouls } from "../format/formatSouls";
import { AssetList } from "./AssetList";
import { FindingCard } from "./FindingCard";
import type { FindingProps } from "./FindingProps";
import { iconSize } from "./iconSize";

// Множественное число — ICU-строкой со значениями в дескрипторе (см. shared/api/errors).
const TITLE = msg({
  id: "finding.item_bought_late",
  message: "{item}: bought {minutes, plural, one {# minute} other {# minutes}} late",
});

const DETAILS = msg({
  id: "finding.item_bought_late.details",
  message:
    "You bought it at {bought}, strong players on this hero buy it at {baseline} on average. {pickRate} of them take it.",
});

const SUMMARY = msg({
  id: "finding.item_bought_late.summary",
  message: "At {bought}, usually {baseline}",
});

// Опоздание по душам к покупке (разборы с 01.11.2026).
const TITLE_SOULS = msg({
  id: "finding.item_bought_late.souls",
  message: "{item}: bought late — at {souls} souls",
});
const DETAILS_SOULS = msg({
  id: "finding.item_bought_late.souls_details",
  message:
    "You bought it at {souls} souls; {share} of strong players on this hero had bought it with fewer. Half of them buy it by {baseline}.",
});
const SUMMARY_SOULS = msg({
  id: "finding.item_bought_late.souls_summary",
  message: "At {souls} souls, half of strong players by {baseline}",
});

// Почему поздно: души ушли на другие предметы или их не хватало.
const OTHER_ITEMS_FIRST = msg({
  id: "finding.item_bought_late.other_items_first",
  message:
    "Before it, {souls, plural, one {# soul} other {# souls}} went to items strong players buy rarely or later:",
});

const NOT_ENOUGH_SOULS = msg({
  id: "finding.item_bought_late.not_enough_souls",
  message: "You did not have enough souls for it: farm was behind.",
});

// Ключевой предмет куплен заметно позже, чем у сильных игроков на том же герое.
export const ItemBoughtLate = ({ finding, variant }: FindingProps<"item_bought_late">) => {
  const { i18n } = useLingui();
  const findItem = useAssetLookup("items");
  const item = findItem(finding.params.item_id);
  const { cause, spent_elsewhere: spent, items_before: itemsBefore } = finding.params;
  const { bought_at_s: boughtAt, baseline_at_s: baselineAt, pick_rate: pickRate } = finding.params;
  const { souls, baseline_souls: baselineSouls, later_than: laterThan } = finding.params;
  const minutes = Math.max(1, Math.round((boughtAt - baselineAt) / 60));
  const bought = formatGameTime(boughtAt);
  const baseline = formatGameTime(baselineAt);
  const bySouls =
    souls != null && baselineSouls != null && laterThan != null
      ? {
          souls: formatSouls(souls, i18n.locale),
          baseline: formatSouls(baselineSouls, i18n.locale),
          share: formatPickRate(laterThan, i18n.locale),
        }
      : null;
  return (
    <FindingCard
      code={finding.code}
      severity={finding.severity}
      variant={variant}
      icon={<AssetIcon asset={item} size={iconSize(variant)} />}
      title={
        bySouls === null
          ? i18n._({ ...TITLE, values: { item: item.name, minutes } })
          : i18n._({ ...TITLE_SOULS, values: { item: item.name, souls: bySouls.souls } })
      }
      details={
        bySouls === null
          ? i18n._({
              ...DETAILS,
              values: { bought, baseline, pickRate: formatPickRate(pickRate, i18n.locale) },
            })
          : i18n._({ ...DETAILS_SOULS, values: bySouls })
      }
      summary={
        bySouls === null
          ? i18n._({ ...SUMMARY, values: { bought, baseline } })
          : i18n._({ ...SUMMARY_SOULS, values: bySouls })
      }
    >
      {cause === "other_items_first" && (
        <AssetList
          label={i18n._({ ...OTHER_ITEMS_FIRST, values: { souls: spent } })}
          entries={itemsBefore.map((id) => ({ asset: findItem(id) }))}
        />
      )}
      {cause === "not_enough_souls" && (
        <p className="mt-1 text-xs text-subtle">{i18n._(NOT_ENOUGH_SOULS)}</p>
      )}
    </FindingCard>
  );
};
